import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Star,
    Quote,
    User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberSpotlightsPage = () => {
    const [spotlights, setSpotlights] = useState([]);
    const [featuredSpotlight, setFeaturedSpotlight] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSpotlights();
    }, []);

    const fetchSpotlights = async () => {
        try {
            const response = await axios.get(`${API}/member/spotlights`);
            const data = response.data;
            const featured = data.find(s => s.is_featured);
            setFeaturedSpotlight(featured || null);
            setSpotlights(data.filter(s => !s.is_featured));
        } catch (error) {
            console.error('Error fetching spotlights:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-[#A0A0A0] animate-pulse">Loading spotlights...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="member-spotlights">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white font-['Playfair_Display'] flex items-center gap-3">
                    <Star className="w-8 h-8 text-[#C9A227]" />
                    Brother Spotlights
                </h1>
                <p className="text-[#A0A0A0] mt-1">
                    Celebrating the stories and achievements of our brothers
                </p>
            </div>

            {/* Featured Spotlight */}
            {featuredSpotlight && (
                <Card className="bg-gradient-to-br from-[#4A5240] to-[#3D4435] border-[#C9A227]/30 overflow-hidden">
                    <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                    <CardContent className="p-6 lg:p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <Star className="w-5 h-5 text-[#C9A227] fill-[#C9A227]" />
                            <span className="text-[#C9A227] text-sm font-medium uppercase tracking-wider">Featured Brother</span>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-1">
                                {featuredSpotlight.image_url ? (
                                    <img 
                                        src={featuredSpotlight.image_url} 
                                        alt={featuredSpotlight.member?.name}
                                        className="w-full aspect-square rounded-xl object-cover"
                                    />
                                ) : (
                                    <div className="w-full aspect-square rounded-xl bg-[#C9A227] flex items-center justify-center text-[#3D4435] text-6xl font-bold">
                                        {featuredSpotlight.member?.name?.charAt(0)}
                                    </div>
                                )}
                            </div>
                            <div className="lg:col-span-2">
                                <h2 className="text-2xl lg:text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
                                    {featuredSpotlight.member?.name}
                                </h2>
                                {featuredSpotlight.title && (
                                    <p className="text-[#C9A227] font-medium mb-4">{featuredSpotlight.title}</p>
                                )}
                                {featuredSpotlight.member?.profession && (
                                    <p className="text-[#A0A0A0] text-sm mb-4">{featuredSpotlight.member.profession}</p>
                                )}
                                <p className="text-[#A0A0A0] leading-relaxed mb-6">{featuredSpotlight.bio}</p>
                                
                                {featuredSpotlight.quote && (
                                    <div className="bg-[#3D4435] rounded-xl p-5 border border-[#5A6350]">
                                        <Quote className="w-6 h-6 text-[#C9A227] mb-3" />
                                        <p className="text-white italic text-lg leading-relaxed">
                                            "{featuredSpotlight.quote}"
                                        </p>
                                    </div>
                                )}

                                {featuredSpotlight.advice && (
                                    <div className="mt-4 p-4 bg-[#C9A227]/10 rounded-xl border border-[#C9A227]/20">
                                        <p className="text-[#C9A227] text-sm font-medium mb-1">Advice for Brothers:</p>
                                        <p className="text-white">{featuredSpotlight.advice}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All Spotlights */}
            {spotlights.length > 0 && (
                <div>
                    <h2 className="text-xl font-bold text-white font-['Playfair_Display'] mb-4">
                        {featuredSpotlight ? 'More Brothers' : 'All Spotlights'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {spotlights.map((spotlight) => (
                            <Card 
                                key={spotlight.id} 
                                className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all overflow-hidden"
                                data-testid={`spotlight-${spotlight.id}`}
                            >
                                <CardContent className="p-0">
                                    {spotlight.image_url ? (
                                        <img 
                                            src={spotlight.image_url} 
                                            alt={spotlight.member?.name}
                                            className="w-full h-48 object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-48 bg-gradient-to-br from-[#C9A227] to-[#B8922A] flex items-center justify-center text-[#3D4435] text-5xl font-bold">
                                            {spotlight.member?.name?.charAt(0)}
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <h3 className="text-white font-semibold text-lg mb-1">
                                            {spotlight.member?.name}
                                        </h3>
                                        {spotlight.title && (
                                            <p className="text-[#C9A227] text-sm font-medium mb-2">{spotlight.title}</p>
                                        )}
                                        {spotlight.member?.profession && (
                                            <p className="text-[#666] text-sm mb-3">{spotlight.member.profession}</p>
                                        )}
                                        <p className="text-[#A0A0A0] text-sm line-clamp-3 mb-3">{spotlight.bio}</p>
                                        
                                        {spotlight.quote && (
                                            <div className="bg-[#3D4435] rounded-lg p-3 border border-[#5A6350]">
                                                <Quote className="w-4 h-4 text-[#C9A227] mb-1" />
                                                <p className="text-white text-sm italic line-clamp-2">
                                                    "{spotlight.quote}"
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!featuredSpotlight && spotlights.length === 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardContent className="p-12 text-center">
                        <Star className="w-16 h-16 text-[#666] mx-auto mb-4" />
                        <h3 className="text-white font-semibold text-xl mb-2">No Spotlights Yet</h3>
                        <p className="text-[#A0A0A0]">
                            Brother spotlights will be featured here as they are added by admins.
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default MemberSpotlightsPage;
